// This composable is used to get service fields for all forms
import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import type { MappedCartItem } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'

export const useServiceFields = () => {
	const {
		findCartItems,
		getFieldPrice,
		findCartItemsInMultipleSubcategories,
		getFieldDescription,
		getAttributeValue,
		multilineWithSingleTotalFields,
		generateOtherLineFieldNames,
		multilineOtherFields,
		otherLineCounts,
	} = useFormFillHelper()

	const hairDresserDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceHairdresser)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const hairDresserPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceHairdresser)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const hairDresserFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceHairdresser
		)

		return [
			...hairDresserDesc(fieldNames.description, items),
			...hairDresserPrice(fieldNames.price, items),
		]
	}

	const embalmingDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceEmbalming)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const embalmingPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceEmbalming)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const embalmingFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceEmbalming
		)

		return [
			...embalmingDesc(fieldNames.description, items),
			...embalmingPrice(fieldNames.price, items),
		]
	}

	const refrigerationDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceRefrigeration)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const refrigerationPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceRefrigeration)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const refrigerationFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceRefrigeration
		)

		return [
			...refrigerationDesc(fieldNames.description, items),
			...refrigerationPrice(fieldNames.price, items),
		]
	}

	const embalmingAndRefrigerationDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.ServiceEmbalming,
				PbSubcategory.ServiceRefrigeration,
			])

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const embalmingAndRefrigerationPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.ServiceEmbalming,
				PbSubcategory.ServiceRefrigeration,
			])

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const embalmingAndRefrigerationFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleSubcategories(PbProductType.Service, [
			PbSubcategory.ServiceEmbalming,
			PbSubcategory.ServiceRefrigeration,
		])

		return [
			...embalmingAndRefrigerationDesc(fieldNames.description, items),
			...embalmingAndRefrigerationPrice(fieldNames.price, items),
		]
	}

	const cremationServicesDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceCremationService
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const cremationServicesPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceCremationService
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const cremationServicesFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceCremationService
		)

		return [
			...cremationServicesDesc(fieldNames.description, items),
			...cremationServicesPrice(fieldNames.price, items),
		]
	}

	const directCremationPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Disposition,
				PbSubcategory.DispositionDirectCremation
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const directBurialPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Disposition,
				PbSubcategory.DispositionDirectBurial
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}
	const visitationDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceVisitationViewing
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const visitationPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceVisitationViewing
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const visitationValue = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceVisitationViewing
			)

		return [
			{
				name: fieldName,
				value: getAttributeValue(items, 'numberOfDays'),
				format: false,
				section: 'service',
			},
		]
	}

	const visitationFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceVisitationViewing
		)

		return [
			...visitationDesc(fieldNames.description, items),
			...visitationPrice(fieldNames.price, items),
		]
	}

	const facilityUseOtherDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceOtherFacilities)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const facilityUseOtherPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceOtherFacilities)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const facilityUseOtherFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceOtherFacilities
		)

		return [
			...facilityUseOtherDesc(fieldNames.description, items),
			...facilityUseOtherPrice(fieldNames.price, items),
		]
	}

	const facilityUseOtherGroupFields = (fieldNames: Record<string, string>) => {
		const items = findCartItemsInMultipleSubcategories(PbProductType.Service, [
			PbSubcategory.ServiceOtherFacilities,
		])

		return multilineWithSingleTotalFields(items, fieldNames, 'service')
	}

	const otherPrepDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceBodyPreparation)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const otherPrepPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceBodyPreparation)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const otherPrepFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceBodyPreparation
		)

		return [
			...otherPrepDesc(fieldNames.description, items),
			...otherPrepPrice(fieldNames.price, items),
		]
	}

	const multilineBodyPreparationFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = [
			...findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceBodyPreparation
			),
		]

		return multilineWithSingleTotalFields(items, fieldNames, 'service')
	}

	const basicServicesDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceBasicServicesOfFDAndStaff
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const basicServicesPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceBasicServicesOfFDAndStaff
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const basicServicesFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceBasicServicesOfFDAndStaff
		)

		return [
			...basicServicesDesc(fieldNames.description, items),
			...basicServicesPrice(fieldNames.price, items),
		]
	}

	const basicServicesAdditionalDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.BasicServicesOfFDAndStaffAdditional
		)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const basicServicesAdditionalPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.BasicServicesOfFDAndStaffAdditional
		)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const basicServicesAdditionalFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.BasicServicesOfFDAndStaffAdditional
		)

		return [
			...basicServicesAdditionalDesc(fieldNames.description, items),
			...basicServicesAdditionalPrice(fieldNames.price, items),
		]
	}

	const funeralMemorialServiceDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceFuneralMemorial)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const funeralMemorialServicePrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceFuneralMemorial)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const funeralMemorialServiceFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceFuneralMemorial
		)

		return [
			...funeralMemorialServiceDesc(fieldNames.description, items),
			...funeralMemorialServicePrice(fieldNames.price, items),
		]
	}

	const memorialServicePrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(PbProductType.Service, PbSubcategory.ServiceMemorialService)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const forwardingRemainsPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceForwardingRemains
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const receivingRemainsPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceReceivingRemains
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const servicesOther1Desc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ?? findCartItems(PbProductType.Service, PbSubcategory.Other)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const servicesOther1Price = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ?? findCartItems(PbProductType.Service, PbSubcategory.Other)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const servicesOther1Fields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(PbProductType.Service, PbSubcategory.Other)

		return [
			...servicesOther1Desc(fieldNames.description, items),
			...servicesOther1Price(fieldNames.price, items),
		]
	}

	const servicesOther2Desc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ?? findCartItems(PbProductType.Service, PbSubcategory.Other2)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'merchandise', // Wisconsin has this field past the service total section
			},
		]
	}

	const servicesOther2Price = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ?? findCartItems(PbProductType.Service, PbSubcategory.Other2)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise', // Wisconsin has this field past the service total section
				supplementalItems: items,
			},
		]
	}

	const servicesOther2Fields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(PbProductType.Service, PbSubcategory.Other2)

		return [
			...servicesOther2Desc(fieldNames.description, items),
			...servicesOther2Price(fieldNames.price, items),
		]
	}

	const servicesOther3Desc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ?? findCartItems(PbProductType.Service, PbSubcategory.Other3)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'merchandise', // Wisconsin has this field past the service total section
			},
		]
	}

	const servicesOther3Price = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ?? findCartItems(PbProductType.Service, PbSubcategory.Other3)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise', // Wisconsin has this field past the service total section
				supplementalItems: items,
			},
		]
	}

	const servicesOther3Fields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(PbProductType.Service, PbSubcategory.Other3)

		return [
			...servicesOther3Desc(fieldNames.description, items),
			...servicesOther3Price(fieldNames.price, items),
		]
	}

	const servicesOther3AndDiscountDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.Other3,
				PbSubcategory.Discount,
			])

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'merchandise', // Wisconsin has this field past the service total section
			},
		]
	}

	const servicesOther3AndDiscountPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.Other3,
				PbSubcategory.Discount,
			])

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'merchandise', // Wisconsin has this field past the service total section
				supplementalItems: items,
			},
		]
	}

	const servicesOther3AndDiscountFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleSubcategories(PbProductType.Service, [
			PbSubcategory.Other3,
			PbSubcategory.Discount,
		])

		return [
			...servicesOther3AndDiscountDesc(fieldNames.description, items),
			...servicesOther3AndDiscountPrice(fieldNames.price, items),
		]
	}

	const additionalServicesDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.Other,
				PbSubcategory.Discount,
			])

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const additionalServicesPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.Other,
				PbSubcategory.Discount,
			])

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const additionalServicesFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleSubcategories(PbProductType.Service, [
			PbSubcategory.Other,
			PbSubcategory.Discount,
		])

		return [
			...additionalServicesDesc(fieldNames.description, items),
			...additionalServicesPrice(fieldNames.price, items),
		]
	}

	const multilineServiceOtherFields = (
		fieldNames: string
	): FieldValueResult[] => {
		if (!otherLineCounts.service) {
			console.error('Service other line count not set')

			return []
		}

		const items = [
			...findCartItems(PbProductType.Service, PbSubcategory.Discount),
			...findCartItems(PbProductType.Service, PbSubcategory.Other),
		]

		const fields = generateOtherLineFieldNames(
			fieldNames,
			otherLineCounts.service
		)

		return multilineOtherFields(fields, items, 'service')
	}

	const multilineServiceOtherFieldsWithTotal = (
		fieldNames: Record<string, string>
	) => {
		if (!otherLineCounts.service) {
			console.error('Service other line count not set')

			return []
		}

		const items = [
			...findCartItems(PbProductType.Service, PbSubcategory.Discount),
			...findCartItems(PbProductType.Service, PbSubcategory.Other),
		]

		const fields = generateOtherLineFieldNames(
			fieldNames.otherLine,
			otherLineCounts.service
		)

		return multilineOtherFields(fields, items, 'service', fieldNames.total)
	}

	const gravesideServiceDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceGravesideService
			)

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'service',
			},
		]
	}

	const gravesideServicePrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItems(
				PbProductType.Service,
				PbSubcategory.ServiceGravesideService
			)

		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'service',
				supplementalItems: items,
			},
		]
	}

	const gravesideServiceFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItems(
			PbProductType.Service,
			PbSubcategory.ServiceGravesideService
		)

		return [
			...gravesideServiceDesc(fieldNames.description, items),
			...gravesideServicePrice(fieldNames.price, items),
		]
	}

	const singleLineOtherServicesDesc = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.Discount,
				PbSubcategory.Other,
			])

		return [
			{
				name: fieldName,
				value: getFieldDescription(items),
				format: false,
				section: 'otherServices',
			},
		]
	}

	const singleLineOtherServicesPrice = (
		fieldName: string,
		passedItems?: MappedCartItem[]
	): FieldValueResult[] => {
		const items =
			passedItems ??
			findCartItemsInMultipleSubcategories(PbProductType.Service, [
				PbSubcategory.Discount,
				PbSubcategory.Other,
			])
		return [
			{
				name: fieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'otherServices',
				supplementalItems: items,
			},
		]
	}

	const singleLineOtherServicesFields = (
		fieldNames: Record<string, string>
	): FieldValueResult[] => {
		const items = findCartItemsInMultipleSubcategories(PbProductType.Service, [
			PbSubcategory.Discount,
			PbSubcategory.Other,
		])
		return [
			...singleLineOtherServicesDesc(fieldNames.description, items),
			...singleLineOtherServicesPrice(fieldNames.price, items),
		]
	}
	return {
		embalmingDesc,
		embalmingPrice,
		embalmingFields,
		refrigerationDesc,
		refrigerationPrice,
		refrigerationFields,
		embalmingAndRefrigerationDesc,
		embalmingAndRefrigerationPrice,
		embalmingAndRefrigerationFields,
		hairDresserDesc,
		hairDresserPrice,
		hairDresserFields,
		cremationServicesDesc,
		cremationServicesPrice,
		cremationServicesFields,
		directCremationPrice,
		directBurialPrice,
		visitationDesc,
		visitationPrice,
		visitationValue,
		visitationFields,
		facilityUseOtherDesc,
		facilityUseOtherPrice,
		facilityUseOtherFields,
		facilityUseOtherGroupFields,
		otherPrepDesc,
		otherPrepPrice,
		otherPrepFields,
		multilineBodyPreparationFields,
		basicServicesDesc,
		basicServicesPrice,
		basicServicesFields,
		basicServicesAdditionalDesc,
		basicServicesAdditionalPrice,
		basicServicesAdditionalFields,
		funeralMemorialServiceDesc,
		funeralMemorialServicePrice,
		funeralMemorialServiceFields,
		memorialServicePrice,
		forwardingRemainsPrice,
		receivingRemainsPrice,
		servicesOther1Desc,
		servicesOther1Price,
		servicesOther1Fields,
		servicesOther2Desc,
		servicesOther2Price,
		servicesOther2Fields,
		servicesOther3Desc,
		servicesOther3Price,
		servicesOther3Fields,
		servicesOther3AndDiscountDesc,
		servicesOther3AndDiscountPrice,
		servicesOther3AndDiscountFields,
		additionalServicesFields,
		multilineServiceOtherFields,
		multilineServiceOtherFieldsWithTotal,
		gravesideServiceDesc,
		gravesideServicePrice,
		gravesideServiceFields,
		singleLineOtherServicesDesc,
		singleLineOtherServicesPrice,
		singleLineOtherServicesFields,
	}
}
